import type { OAuthCredential } from 'firebase/auth';
import { linkWithCredential, signInWithEmailAndPassword } from 'firebase/auth';
import { auth } from '@/lib/firebase';

/**
 * Error thrown when sign-in with a provider (Google/Apple) fails because
 * that credential is already associated with a different Firebase user.
 * The app can prompt the user to sign in with their existing email/password
 * and then link the provider to that account.
 */
export class AccountLinkingRequiredError extends Error {
  readonly code = 'auth/credential-already-in-use';
  readonly email: string;
  readonly credential: OAuthCredential;
  readonly providerLabel: 'Google' | 'Apple';

  constructor(email: string, credential: OAuthCredential, providerLabel: 'Google' | 'Apple') {
    super(
      `An account with ${email} already exists. Sign in with your password to link your ${providerLabel} account.`,
    );
    this.name = 'AccountLinkingRequiredError';
    this.email = email;
    this.credential = credential;
    this.providerLabel = providerLabel;
  }
}

function decodeIdTokenPayload(idToken: string): Record<string, unknown> | null {
  try {
    const parts = idToken.split('.');
    if (parts.length !== 3) return null;
    const base64url = parts[1];
    const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const json =
      typeof globalThis !== 'undefined' && globalThis.atob
        ? globalThis.atob(padded)
        : (typeof Buffer !== 'undefined' && Buffer.from(padded, 'base64').toString('utf8')) || '';
    const payload = JSON.parse(json);
    return payload && typeof payload === 'object' ? (payload as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

/**
 * Decode email from a JWT id_token (e.g. Google or Apple).
 * Returns null if payload is missing or email is not present.
 */
export function getEmailFromIdToken(idToken: string): string | null {
  const payload = decodeIdTokenPayload(idToken);
  if (!payload) return null;
  const email = payload.email;
  return typeof email === 'string' && email.length > 0 ? email : null;
}

/**
 * Decode display name from a JWT id_token (e.g. Google's `name` claim).
 */
export function getNameFromIdToken(idToken: string): string | null {
  const payload = decodeIdTokenPayload(idToken);
  if (!payload) return null;
  const name = payload.name;
  const s = typeof name === 'string' ? name.trim() : '';
  return s.length > 0 ? s : null;
}

/**
 * Sign in with email/password and link the given OAuth credential to that account.
 * Use this when the user chose "link account" after seeing credential-already-in-use.
 */
export async function signInAndLinkCredential(
  email: string,
  password: string,
  credential: OAuthCredential,
): Promise<void> {
  const userCredential = await signInWithEmailAndPassword(auth, email.trim(), password);
  await linkWithCredential(userCredential.user, credential);
}
